import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, FileText, Play, Database, Tags, Store,
  Users, Briefcase, Calendar, UserX, FileOutput, Settings, 
  LogOut, Building2, Menu, X, Lock, Bell, Shield, FolderTree, ChevronDown, ChevronRight
} from 'lucide-react';
import { useState, useEffect } from 'react';
import BRANDING from '@/config/branding';
import { getNavigation } from '@/config/appModes';
import { API } from '@/App';

const iconMap = {
  LayoutDashboard,
  FileText,
  Play,
  Database,
  Tags,
  Store,
  Users,
  Briefcase,
  Calendar,
  UserX,
  FileOutput,
  Settings,
  Building2,
  FolderTree,
  ChevronDown,
  ChevronRight,
};

// Define which features are available in which plans
const PLAN_FEATURES = {
  free: ['dashboard', 'templates', 'runs'],
  pro: ['dashboard', 'templates', 'runs', 'global-fields', 'categories'],
  business: ['dashboard', 'templates', 'runs', 'global-fields', 'categories', 'marketplace'],
  crm: ['dashboard', 'clients', 'cases', 'courts', 'forms', 'templates', 'runs', 'calendar', 'opposing-counsel', 'global-fields', 'categories', 'marketplace', 'firm']
};

export default function Sidebar({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Start collapsed
  
  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const appMode = user?.app_mode || 'standard';
  const userPlan = user?.subscription_plan || 'free';
  const navItems = getNavigation(appMode);
  
  // Get all navigation items regardless of plan
  const allNavItems = appMode === 'crm' ? getNavigation('crm') : getNavigation('standard');
  
  // Fetch notifications count
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/notifications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    if (onLogout) onLogout();
    navigate('/');
  };

  const handleNavigate = (path, isLocked) => {
    if (isLocked) {
      navigate('/pricing');
    } else {
      navigate(path);
    }
    setIsMobileOpen(false);
  };

  const isFeatureLocked = (itemId) => {
    const availableFeatures = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
    return !availableFeatures.includes(itemId);
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div 
          className="flex items-center gap-3 cursor-pointer" 
          onClick={() => navigate('/dashboard')}
        >
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${BRANDING.colors.primaryGradient} flex items-center justify-center`}>
            <FileText className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-900">
            {BRANDING.appName}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="lg:hidden"
        >
          {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-screen bg-white border-r border-slate-200 z-40
          transition-transform duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          w-64 flex flex-col
        `}
      >
        {/* Logo */}
        <div className="h-16 border-b border-slate-200 flex items-center px-6 flex-shrink-0">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => handleNavigate('/dashboard')}
            data-testid="nav-logo"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_courts-module/artifacts/ttoifebz_twenty-docs-logo-small.jpg"
              alt="TwentyDocs"
              className="w-10 h-10 rounded-lg"
            />
            <div>
              <div>
                <span className="text-lg font-bold text-slate-900 block leading-tight">
                  {BRANDING.appName}
                </span>
                <span className="text-[9px] text-slate-500 block leading-tight -mt-0.5">
                  A Twenty Digital Product
                </span>
              </div>
              {appMode === 'crm' && (
                <span 
                  className="text-xs font-semibold text-violet-600 block mt-1"
                  data-testid="crm-mode-badge"
                >
                  CRM Mode
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            {allNavItems.map((item) => {
              // Handle grouped items
              if (item.isGroup) {
                const Icon = iconMap[item.icon] || Settings;
                const isExpanded = expandedGroups.has(item.id);
                
                return (
                  <div key={item.id}>
                    <Button
                      variant="ghost"
                      onClick={() => toggleGroup(item.id)}
                      className="w-full justify-start rounded-xl h-11 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </Button>
                    
                    {/* Sub-items */}
                    {isExpanded && item.items && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.items.map((subItem) => {
                          const SubIcon = iconMap[subItem.icon] || FileText;
                          const isActive = location.pathname === subItem.path || 
                                          (subItem.path !== '/dashboard' && location.pathname.startsWith(subItem.path));
                          const isLocked = isFeatureLocked(subItem.id);
                          
                          return (
                            <Button
                              key={subItem.id}
                              variant="ghost"
                              onClick={() => handleNavigate(subItem.path, isLocked)}
                              className={`
                                w-full justify-start rounded-xl h-10 text-sm
                                ${isActive && !isLocked
                                  ? 'bg-violet-50 text-violet-700 font-medium' 
                                  : isLocked
                                  ? 'text-slate-400 hover:bg-slate-50 cursor-pointer'
                                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }
                              `}
                              data-testid={`nav-${subItem.id}-btn`}
                            >
                              <SubIcon className="w-4 h-4 mr-3" />
                              <span className="flex-1 text-left">{subItem.label}</span>
                              {isLocked && (
                                <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-500 rounded flex items-center gap-1">
                                  <Lock className="w-2.5 h-2.5" />
                                  Upgrade
                                </span>
                              )}
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // Regular nav items (not grouped)
              const Icon = iconMap[item.icon] || FileText;
              const isActive = location.pathname === item.path || 
                              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
              const isLocked = isFeatureLocked(item.id);
              
              return (
                <div key={item.id} className="relative">
                  <Button
                    variant="ghost"
                    onClick={() => handleNavigate(item.path, isLocked)}
                    className={`
                      w-full justify-start rounded-xl h-11
                      ${isActive && !isLocked
                        ? 'bg-violet-50 text-violet-700 font-medium' 
                        : isLocked
                        ? 'text-slate-400 hover:bg-slate-50 cursor-pointer'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }
                    `}
                    data-testid={`nav-${item.id}-btn`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {isLocked && (
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-500 rounded flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" />
                        Upgrade
                      </span>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Bottom Section - Notifications, Settings & Logout */}
        <div className="border-t border-slate-200 p-3 space-y-1 flex-shrink-0">
          {/* Super Admin Toggle - Only for super admins */}
          {user?.is_super_admin && (
            <Button
              variant="ghost"
              onClick={() => handleNavigate(location.pathname.startsWith('/admin') ? '/dashboard' : '/admin')}
              className={`
                w-full justify-start rounded-xl h-11
                ${location.pathname.startsWith('/admin')
                  ? 'bg-violet-600 text-white hover:bg-violet-700' 
                  : 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200'
                }
              `}
              data-testid="super-admin-toggle"
            >
              <Shield className="w-5 h-5 mr-3" />
              {location.pathname.startsWith('/admin') ? 'Exit Admin Mode' : 'Admin Panel'}
            </Button>
          )}
          
          <Button
            variant="ghost"
            onClick={() => handleNavigate('/notifications')}
            className={`
              w-full justify-start rounded-xl h-11 relative
              ${location.pathname === '/notifications'
                ? 'bg-violet-50 text-violet-700 font-medium' 
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }
            `}
            data-testid="nav-notifications-btn"
          >
            <Bell className="w-5 h-5 mr-3" />
            Notifications
            {unreadCount > 0 && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => handleNavigate('/settings')}
            className={`
              w-full justify-start rounded-xl h-11
              ${location.pathname === '/settings'
                ? 'bg-violet-50 text-violet-700 font-medium' 
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }
            `}
            data-testid="nav-settings-btn"
          >
            <Settings className="w-5 h-5 mr-3" />
            Settings
          </Button>
          
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start rounded-xl h-11 text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
    </>
  );
}
